import { useMemo } from "react";
import {
  BaseEdge, EdgeLabelRenderer,
  type Edge as FlowEdge, type EdgeProps, type Node as FlowNode, useNodes, useReactFlow,
} from "@xyflow/react";
import type { GraphEdge, GraphNode, ID } from "@/types";
import { edgeMeta } from "@/lib/vocabulary";
import {
  chooseLabelPoint, clamp, estimateEdgeLabelSize, expandedRect,
  pointAtPolylineFraction, polylineLength, roundedPolylinePath, segmentIntersectsRect,
  type MeasuredRect, type Point, type Rect,
} from "@/lib/edge-geometry";
import { cn } from "@/lib/cn";

export interface EdgeCritiqueCard {
  node: GraphNode;
  edge: GraphEdge;
  isSelected: boolean;
}

export interface StrainEdgeData {
  edge: GraphEdge;
  isSelected: boolean;
  isHighlighted: boolean;
  isDimmed: boolean;
  hiddenCritiqueCount?: number;
  revealNodeId?: ID;
  onRevealCritiques?: (nodeId?: ID, edgeId?: ID) => void;
  critiqueCards?: EdgeCritiqueCard[];
  onSelectNode?: (nodeId: ID) => void;
  [key: string]: unknown;
}

const NODE_CLEARANCE = 16;
const MIN_STEM = 34;
const PARALLEL_GAP = 22;
const PORT_GAP = 24;

function rectForFlowNode(node: FlowNode): MeasuredRect {
  const width = node.measured?.width ?? node.width ?? 280;
  const height = node.measured?.height ?? node.height ?? 110;
  const left = node.position.x;
  const top = node.position.y;
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
    cx: left + width / 2,
    cy: top + height / 2,
  };
}

function estimateLabelSize(label: string, showFullLabel: boolean, undercuts = 0): { w: number; h: number } {
  if (!showFullLabel) return { w: undercuts > 0 ? 38 : 22, h: 20 };
  return estimateEdgeLabelSize(label, undercuts);
}

function routeScore(points: Point[], nodeRects: Rect[]): number {
  let intersections = 0;
  for (let i = 1; i < points.length; i += 1) {
    for (const rect of nodeRects) {
      if (segmentIntersectsRect(points[i - 1], points[i], expandedRect(rect, NODE_CLEARANCE))) {
        intersections += 1;
      }
    }
  }
  return intersections * 100000 + polylineLength(points) + points.length * 12;
}

function relationRank(edge: GraphEdge): number {
  switch (edge.relation) {
    case "defines": return 0;
    case "evidences": return 1;
    case "supports": return 2;
    case "qualifies": return 3;
    case "reframes": return 4;
    case "rebuts": return 5;
    case "credibility": return 6;
    case "undercuts": return 7;
    default: return 8;
  }
}

function visibleGraphEdge(flowEdge: FlowEdge): GraphEdge | null {
  const edge = (flowEdge.data as StrainEdgeData | undefined)?.edge;
  return edge ?? null;
}

function portOffset(
  edge: GraphEdge,
  nodeId: ID,
  role: "source" | "target",
  rectById: Map<ID, MeasuredRect>,
  flowEdges: FlowEdge[],
  rect: MeasuredRect,
): number {
  const siblings = flowEdges
    .map(visibleGraphEdge)
    .filter((item): item is GraphEdge => {
      if (!item || item.target_edge || !item.target) return false;
      return role === "source" ? item.source === nodeId : item.target === nodeId;
    })
    .sort((a, b) => {
      const otherA = role === "source" ? a.target : a.source;
      const otherB = role === "source" ? b.target : b.source;
      const rectA = otherA ? rectById.get(otherA) : null;
      const rectB = otherB ? rectById.get(otherB) : null;
      const dy = (rectA?.cy ?? 0) - (rectB?.cy ?? 0);
      if (Math.abs(dy) > 1) return dy;
      const rank = relationRank(a) - relationRank(b);
      if (rank !== 0) return rank;
      return a.id.localeCompare(b.id);
    });

  if (siblings.length <= 1) return 0;
  const index = siblings.findIndex((item) => item.id === edge.id);
  if (index < 0) return 0;
  const span = Math.max(0, rect.height - 44);
  return clamp((index - (siblings.length - 1) / 2) * PORT_GAP, -span / 2, span / 2);
}

function laneOffset(
  edge: GraphEdge,
  sourceRect: MeasuredRect,
  targetRect: MeasuredRect,
  rectById: Map<ID, MeasuredRect>,
  flowEdges: FlowEdge[],
): number {
  if (!edge.target || edge.target_edge) return 0;
  const sourceColumn = Math.round(sourceRect.cx / 180);
  const targetColumn = Math.round(targetRect.cx / 180);
  const direction = targetRect.cx >= sourceRect.cx ? "right" : "left";
  const siblings = flowEdges
    .map(visibleGraphEdge)
    .filter((item): item is GraphEdge => {
      if (!item || item.target_edge || !item.target) return false;
      const siblingSource = rectById.get(item.source);
      const siblingTarget = rectById.get(item.target);
      if (!siblingSource || !siblingTarget) return false;
      const siblingDirection = siblingTarget.cx >= siblingSource.cx ? "right" : "left";
      return (
        siblingDirection === direction
        && Math.round(siblingSource.cx / 180) === sourceColumn
        && Math.round(siblingTarget.cx / 180) === targetColumn
      );
    })
    .sort((a, b) => {
      const sy = (rectById.get(a.source)?.cy ?? 0) - (rectById.get(b.source)?.cy ?? 0);
      if (Math.abs(sy) > 1) return sy;
      return a.id.localeCompare(b.id);
    });

  if (siblings.length <= 1) return 0;
  const index = siblings.findIndex((item) => item.id === edge.id);
  if (index < 0) return 0;
  return (index - (siblings.length - 1) / 2) * PARALLEL_GAP;
}

function routeYCandidates(start: Point, end: Point, sourceRect: Rect, targetRect: Rect, obstacles: Rect[]) {
  const minX = Math.min(start.x, end.x) - NODE_CLEARANCE;
  const maxX = Math.max(start.x, end.x) + NODE_CLEARANCE;
  const relevant = obstacles.filter((rect) => rect.right >= minX && rect.left <= maxX);
  const top = Math.min(sourceRect.top, targetRect.top, ...relevant.map((rect) => rect.top)) - 54;
  const bottom = Math.max(sourceRect.bottom, targetRect.bottom, ...relevant.map((rect) => rect.bottom)) + 54;
  return [
    (start.y + end.y) / 2,
    Math.max(16, top),
    bottom,
    Math.max(16, top - 72),
    bottom + 72,
  ];
}

function chooseBestRoute(candidates: Point[][], nodeRects: Rect[]): Point[] {
  let best = candidates[0];
  let bestScore = Number.POSITIVE_INFINITY;
  for (const points of candidates) {
    const score = routeScore(points, nodeRects);
    if (score < bestScore) {
      best = points;
      bestScore = score;
    }
  }
  return best;
}

function routeBetweenRects({
  sourceRect,
  targetRect,
  start,
  end,
  lane,
  obstacles,
}: {
  sourceRect: MeasuredRect;
  targetRect: MeasuredRect;
  start: Point;
  end: Point;
  lane: number;
  obstacles: Rect[];
}): Point[] {
  const candidates: Point[][] = [];
  const leftToRight = end.x >= start.x;
  const horizontalGap = leftToRight ? targetRect.left - sourceRect.right : sourceRect.left - targetRect.right;

  if (horizontalGap > 108) {
    const minLane = leftToRight ? sourceRect.right + 42 : targetRect.right + 42;
    const maxLane = leftToRight ? targetRect.left - 42 : sourceRect.left - 42;
    const baseLane = leftToRight
      ? sourceRect.right + horizontalGap / 2
      : targetRect.right + horizontalGap / 2;
    const laneCandidates = [
      clamp(baseLane + lane, minLane, maxLane),
      clamp(baseLane + lane + PARALLEL_GAP, minLane, maxLane),
      clamp(baseLane + lane - PARALLEL_GAP, minLane, maxLane),
      clamp(baseLane, minLane, maxLane),
    ];
    for (const laneX of laneCandidates) {
      candidates.push([start, { x: laneX, y: start.y }, { x: laneX, y: end.y }, end]);
    }

    const sourceStemX = leftToRight ? sourceRect.right + MIN_STEM : sourceRect.left - MIN_STEM;
    const targetStemX = leftToRight ? targetRect.left - MIN_STEM : targetRect.right + MIN_STEM;
    for (const y of routeYCandidates(start, end, sourceRect, targetRect, obstacles)) {
      candidates.push([
        start,
        { x: sourceStemX, y: start.y },
        { x: sourceStemX, y },
        { x: targetStemX, y },
        { x: targetStemX, y: end.y },
        end,
      ]);
    }
  } else {
    const sourceStemX = leftToRight
      ? Math.max(sourceRect.right + MIN_STEM, sourceRect.cx + MIN_STEM)
      : Math.min(sourceRect.left - MIN_STEM, sourceRect.cx - MIN_STEM);
    const targetStemX = leftToRight
      ? Math.max(targetRect.right + MIN_STEM, targetRect.cx + MIN_STEM)
      : Math.min(targetRect.left - MIN_STEM, targetRect.cx - MIN_STEM);
    for (const y of routeYCandidates(start, end, sourceRect, targetRect, obstacles)) {
      candidates.push([
        start,
        { x: sourceStemX, y: start.y },
        { x: sourceStemX, y },
        { x: targetStemX, y },
        { x: targetStemX, y: end.y },
        end,
      ]);
    }
  }

  return chooseBestRoute(candidates, obstacles);
}

function StrainEdgeComponent({
  id, sourceX, sourceY, targetX, targetY, data, markerEnd,
}: EdgeProps) {
  const d = data as StrainEdgeData;
  const edge = d.edge;
  const meta = edgeMeta(edge.relation);
  const rf = useReactFlow();
  // reactive node list: routes re-evaluate when ANY node moves or re-measures,
  // so obstacle avoidance doesn't go stale while unrelated nodes are dragged
  const flowNodes = useNodes();

  const geom = useMemo(() => {
    const flowEdges = rf.getEdges();
    const rectById = new Map<ID, MeasuredRect>();
    flowNodes.forEach((node) => rectById.set(node.id, rectForFlowNode(node)));

    const sourceRect = rectById.get(edge.source);
    if (!sourceRect) {
      const points = [{ x: sourceX, y: sourceY }, { x: targetX, y: targetY }];
      return {
        sx: sourceX,
        sy: sourceY,
        tx: targetX,
        ty: targetY,
        points,
        path: roundedPolylinePath(points),
        isUndercut: false,
      };
    }

    let targetRect = edge.target ? rectById.get(edge.target) : undefined;
    let isUndercut = false;

    if (edge.relation === "undercuts" && edge.target_edge) {
      isUndercut = true;
      const targetEdge = flowEdges.find((item) => item.id === edge.target_edge);
      const targetGraphEdge = targetEdge ? visibleGraphEdge(targetEdge) : null;
      const targetSourceRect = targetGraphEdge ? rectById.get(targetGraphEdge.source) : null;
      const targetTargetRect = targetGraphEdge?.target ? rectById.get(targetGraphEdge.target) : null;
      if (targetSourceRect && targetTargetRect) {
        const midpoint = {
          x: (targetSourceRect.right + targetTargetRect.left) / 2,
          y: (targetSourceRect.cy + targetTargetRect.cy) / 2,
        };
        targetRect = {
          left: midpoint.x - 1,
          right: midpoint.x + 1,
          top: midpoint.y - 1,
          bottom: midpoint.y + 1,
          width: 2,
          height: 2,
          cx: midpoint.x,
          cy: midpoint.y,
        };
      }
    }

    if (!targetRect) {
      const points = [{ x: sourceRect.right, y: sourceRect.cy }, { x: targetX, y: targetY }];
      return {
        sx: points[0].x,
        sy: points[0].y,
        tx: targetX,
        ty: targetY,
        points,
        path: roundedPolylinePath(points),
        isUndercut,
      };
    }

    const sourceOffset = portOffset(edge, edge.source, "source", rectById, flowEdges, sourceRect);
    const targetOffset = edge.target
      ? portOffset(edge, edge.target, "target", rectById, flowEdges, targetRect)
      : 0;
    const leftToRight = targetRect.cx >= sourceRect.cx;
    const sourceSideX = leftToRight ? sourceRect.right : sourceRect.left;
    const targetSideX = leftToRight ? targetRect.left : targetRect.right;
    const sourcePortY = clamp(sourceRect.cy + sourceOffset, sourceRect.top + 22, sourceRect.bottom - 22);
    const targetPortY = isUndercut
      ? targetRect.cy
      : clamp(targetRect.cy + targetOffset, targetRect.top + 22, targetRect.bottom - 22);
    const start = { x: sourceSideX, y: sourcePortY };
    const end = { x: targetSideX, y: targetPortY };
    const obstacles = Array.from(rectById.entries())
      .filter(([nodeId]) => nodeId !== edge.source && nodeId !== edge.target)
      .map(([, rect]) => rect);
    const lane = laneOffset(edge, sourceRect, targetRect, rectById, flowEdges);
    const points = routeBetweenRects({ sourceRect, targetRect, start, end, lane, obstacles });

    return {
      sx: start.x,
      sy: start.y,
      tx: end.x,
      ty: end.y,
      points,
      path: roundedPolylinePath(points),
      isUndercut,
    };
  }, [edge, flowNodes, rf, sourceX, sourceY, targetX, targetY]);

  const strokeDasharray =
    meta.stroke === "dashed" ? "8 6"
    : meta.stroke === "dotted" ? "2.5 5.5"
    : undefined;

  const Icon = meta.icon;
  const showFullLabel = d.isSelected || d.isHighlighted;
  const hiddenCritiqueCount = d.hiddenCritiqueCount ?? 0;
  const hasHiddenCritiques = hiddenCritiqueCount > 0;
  const critiqueCards = d.critiqueCards ?? [];
  const hasCritiqueCards = critiqueCards.length > 0;
  const showCompactLabel = geom.isUndercut || hasHiddenCritiques || (edge.stats?.undercuts ?? 0) > 0;
  const showLabel = showFullLabel || showCompactLabel;
  const visualWidth = meta.strokeWidth + 1.15;
  const activeWidth = visualWidth + 1.4;
  const labelText = edge.label ?? meta.verb;
  const labelSize = estimateLabelSize(labelText, showFullLabel, hiddenCritiqueCount || edge.stats?.undercuts || 0);
  const baseLabelPoint = pointAtPolylineFraction(geom.points, 0.5);
  const labelPoint = useMemo(() => {
    const nodeRects = flowNodes.map((n) => rectForFlowNode(n));
    return chooseLabelPoint({
      baseX: baseLabelPoint.x,
      baseY: baseLabelPoint.y,
      sx: geom.sx,
      sy: geom.sy,
      tx: geom.tx,
      ty: geom.ty,
      labelW: labelSize.w,
      labelH: labelSize.h,
      nodeRects,
    }).point;
  }, [flowNodes, baseLabelPoint.x, baseLabelPoint.y, geom.sx, geom.sy, geom.tx, geom.ty, labelSize.w, labelSize.h]);
  const baseCardPoint = pointAtPolylineFraction(geom.points, 0.62);
  const cardPoint = useMemo(() => {
    const nodeRects = flowNodes.map((n) => rectForFlowNode(n));
    return chooseLabelPoint({
      baseX: baseCardPoint.x,
      baseY: baseCardPoint.y,
      sx: geom.sx,
      sy: geom.sy,
      tx: geom.tx,
      ty: geom.ty,
      labelW: 286,
      labelH: Math.min(260, Math.max(82, critiqueCards.length * 88)),
      nodeRects,
    }).point;
  }, [flowNodes, baseCardPoint.x, baseCardPoint.y, geom.sx, geom.sy, geom.tx, geom.ty, critiqueCards.length]);

  return (
    <>
      <BaseEdge
        id={id}
        path={geom.path}
        style={{ stroke: "transparent", strokeWidth: 18, fill: "none" }}
        interactionWidth={0}
      />
      <BaseEdge
        id={id + "-glow"}
        path={geom.path}
        style={{
          stroke: d.isDimmed ? "transparent" : meta.color,
          strokeWidth: (d.isSelected || d.isHighlighted ? activeWidth : visualWidth) + 5,
          strokeDasharray,
          strokeLinecap: "round",
          strokeLinejoin: "round",
          fill: "none",
          opacity: d.isSelected || d.isHighlighted ? 0.22 : 0.12,
          transition: "stroke 0.15s, stroke-width 0.15s, opacity 0.15s",
        }}
      />
      <BaseEdge
        id={id + "-viz"}
        path={geom.path}
        markerEnd={markerEnd}
        style={{
          stroke: d.isDimmed ? "var(--color-border)" : meta.color,
          strokeWidth: d.isSelected || d.isHighlighted ? activeWidth : visualWidth,
          strokeDasharray,
          strokeLinecap: "round",
          strokeLinejoin: "round",
          fill: "none",
          opacity: d.isDimmed ? 0.28 : 0.96,
          transition: "stroke 0.15s, stroke-width 0.15s, opacity 0.15s",
        }}
      />
      {!d.isDimmed && (
        <circle
          cx={geom.sx}
          cy={geom.sy}
          r={d.isSelected || d.isHighlighted ? 4.1 : 3.4}
          fill="var(--color-surface)"
          stroke={meta.color}
          strokeWidth={d.isSelected || d.isHighlighted ? 2.1 : 1.5}
          pointerEvents="none"
        />
      )}
      {geom.isUndercut && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${geom.tx}px, ${geom.ty}px)`,
              zIndex: 35,
            }}
            className="pointer-events-none"
          >
            <div className="w-3 h-3 rotate-45 bg-[var(--color-undercut)] border border-[var(--color-base)] shadow-[0_0_8px_rgba(251,191,36,0.6)]" />
          </div>
        </EdgeLabelRenderer>
      )}
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelPoint.x}px, ${labelPoint.y}px)`,
              pointerEvents: "all",
              zIndex: showFullLabel ? 45 : 32,
            }}
            className="nodrag nopan"
          >
            <div
              role={hasHiddenCritiques ? "button" : undefined}
              tabIndex={hasHiddenCritiques ? 0 : undefined}
              onClick={(event) => {
                if (!hasHiddenCritiques) return;
                event.stopPropagation();
                d.onRevealCritiques?.(d.revealNodeId, edge.id);
              }}
              onKeyDown={(event) => {
                if (!hasHiddenCritiques) return;
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                event.stopPropagation();
                d.onRevealCritiques?.(d.revealNodeId, edge.id);
              }}
              className={`group inline-flex items-center gap-1 rounded-md border font-mono uppercase tracking-wider transition-all ${
                d.isSelected
                  ? "px-1.5 py-0.5 text-[10px] border-[var(--color-primary)] bg-[var(--color-surface)] text-[var(--color-ink)] shadow-pop"
                : d.isDimmed
                  ? "px-1.5 py-0.5 text-[10px] border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-ink-faint)] opacity-50"
                : hasHiddenCritiques && !showFullLabel
                  ? "min-h-6 min-w-9 justify-center px-1.5 py-0.5 border-[var(--color-rebut)] bg-[var(--color-surface)] text-[var(--color-rebut)] shadow-card hover:border-[var(--color-rebut)] hover:bg-[color-mix(in_srgb,var(--color-rebut)_12%,var(--color-surface)_88%)] cursor-pointer"
                : showFullLabel
                  ? "max-w-[220px] px-1.5 py-0.5 text-[10px] border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-ink-soft)] shadow-card hover:border-[var(--color-primary)] hover:text-[var(--color-ink)]"
                : "h-5 min-w-5 justify-center px-1 text-[9px] border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-ink-muted)] shadow-card opacity-90 hover:opacity-100 hover:border-[var(--color-primary)]"
              }`}
              style={{ color: hasHiddenCritiques && !showFullLabel ? undefined : d.isSelected || d.isDimmed ? undefined : meta.color }}
              title={hasHiddenCritiques ? `${hiddenCritiqueCount} hidden critique${hiddenCritiqueCount === 1 ? "" : "s"}. Click to inspect.` : meta.description}
            >
              {hasHiddenCritiques && !showFullLabel ? (
                <CritiquePips
                  count={hiddenCritiqueCount}
                  onClick={(event) => {
                    event.stopPropagation();
                    d.onRevealCritiques?.(d.revealNodeId, edge.id);
                  }}
                />
              ) : (
                <>
                  <Icon size={10} />
                  {showFullLabel && labelText}
                  {hasHiddenCritiques ? (
                    <span className="ml-0.5 inline-flex items-center gap-0.5 text-[var(--color-rebut)]">
                      <CritiquePips count={hiddenCritiqueCount} compact />
                      {hiddenCritiqueCount}
                    </span>
                  ) : edge.stats?.undercuts ? (
                    <span className="ml-0.5 text-[var(--color-undercut)]">u{edge.stats.undercuts}</span>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
      {hasCritiqueCards && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${cardPoint.x}px, ${cardPoint.y}px)`,
              pointerEvents: "all",
              zIndex: critiqueCards.some((card) => card.isSelected) ? 62 : 50,
            }}
            className={cn("nodrag nopan flex w-[286px] flex-col gap-1.5 transition-opacity", d.isDimmed && "opacity-30")}
          >
            {critiqueCards.map((card) => {
              const cardMeta = edgeMeta(card.edge.relation);
              const CardIcon = cardMeta.icon;
              return (
                <button
                  key={card.edge.id}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    d.onSelectNode?.(card.node.id);
                  }}
                  className={cn(
                    "w-full rounded-sm border bg-[var(--color-surface)] px-2.5 py-2 text-left shadow-pop transition-all hover:translate-y-[-1px]",
                    card.isSelected && "ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-base)]",
                  )}
                  style={{
                    borderColor: `color-mix(in srgb, ${cardMeta.color} 56%, var(--color-border) 44%)`,
                    background: `color-mix(in srgb, ${cardMeta.color} 9%, var(--color-surface) 91%)`,
                  }}
                  title={cardMeta.description}
                >
                  <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider" style={{ color: cardMeta.color }}>
                    <CardIcon size={10} />
                    <span>{cardMeta.label}</span>
                  </div>
                  <div className="mt-1 max-h-[4.2em] overflow-hidden text-[12px] leading-[1.4] text-[var(--color-ink)]">
                    {card.node.text}
                  </div>
                </button>
              );
            })}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

function CritiquePips({
  count, compact = false, onClick,
}: {
  count: number;
  compact?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}) {
  const visible = Math.min(count, 4);
  const dots = (
    <>
      {Array.from({ length: visible }).map((_, index) => (
        <span
          key={index}
          className={compact ? "h-1.5 w-1.5 rounded-full bg-[var(--color-rebut)]" : "h-2 w-2 rounded-full bg-[var(--color-rebut)]"}
        />
      ))}
      {count > visible && (
        <span className="ml-0.5 text-[9px] leading-none text-[var(--color-rebut)]">+{count - visible}</span>
      )}
    </>
  );
  if (!onClick) {
    return (
      <span className="inline-flex items-center gap-0.5" aria-label={`${count} hidden critiques`}>
        {dots}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-0.5"
      aria-label={`${count} hidden critiques`}
    >
      {dots}
    </button>
  );
}

export const StrainEdge = StrainEdgeComponent;
